import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, apiClient } from '../store/authStore';
import {
  Spinner, CheckCircle, XCircle, Clock, Globe, Trash,
  ArrowRight, ArrowUpRight, MagnifyingGlass, Plus,
  Gauge, Lightning, Link as LinkIcon, Robot
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

/* ─── Design tokens ─────────────────────────────────────────── */
const C = {
  bg:      '#0a0a0f',
  surface: '#13131f',
  surfaceHover: '#181828',
  border:  'rgba(124,58,237,0.2)',
  borderHi:'rgba(124,58,237,0.45)',
  violet:  '#7c3aed',
  blue:    '#3b82f6',
  text:    '#f1f5f9',
  muted:   '#94a3b8',
  faint:   '#475569',
  gradient:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  shadow:  '0 4px 24px rgba(124,58,237,0.1)',
  shadowHi:'0 8px 32px rgba(124,58,237,0.2)',
  font:    "'Inter', system-ui, -apple-system, sans-serif",
};

const card = {
  background:   C.surface,
  border:       `1px solid ${C.border}`,
  borderRadius: 16,
  boxShadow:    C.shadow,
};

const btnGrad = {
  background:   C.gradient,
  color:        '#fff',
  border:       'none',
  borderRadius: 10,
  fontWeight:   700,
  cursor:       'pointer',
  fontFamily:   C.font,
};

/* ─── Nav items (header) ─────────────────────────────────────── */
const NAV = [
  { to: '/dashboard',          label: 'Dashboard' },
  { to: '/seo-action-center',  label: 'Site Audit' },
  { to: '/meta-generator',     label: 'Meta Generator' },
  { to: '/internal-links',     label: 'Internal Links' },
];

/* ─── Quick Actions ──────────────────────────────────────────── */
const QUICK = [
  { to: '/seo-action-center', icon: Gauge,    label: 'Site Audit',       desc: 'Full technical SEO audit' },
  { to: '/meta-generator',    icon: Lightning, label: 'Meta Generator',   desc: 'AI-powered bulk meta tags' },
  { to: '/internal-links',    icon: LinkIcon,  label: 'Internal Links',   desc: 'Detect orphan pages' },
  { to: '/traffic',           icon: Robot,     label: 'AI Traffic',       desc: 'Rewrite titles & keywords' },
];

/* ─── Score badge ────────────────────────────────────────────── */
function ScoreBadge({ score }) {
  if (score == null) return null;
  const n = Number(score);
  const color = n > 70 ? '#10b981' : n > 40 ? '#f59e0b' : '#ef4444';
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color, background: color + '18', border: `1px solid ${color}40`, borderRadius: 8, padding: '2px 9px' }}>
      {n}
    </span>
  );
}

/* ─── StatusIcon ─────────────────────────────────────────────── */
function StatusIcon({ status }) {
  if (status === 'completed')
    return <CheckCircle weight="fill" style={{ width: 14, height: 14, color: '#10b981' }} />;
  if (status === 'running' || status === 'pending')
    return <Spinner style={{ width: 14, height: 14, color: C.violet }} className="animate-spin" />;
  if (status === 'failed')
    return <XCircle weight="fill" style={{ width: 14, height: 14, color: '#ef4444' }} />;
  return <Clock style={{ width: 14, height: 14, color: C.faint }} />;
}

/* ─── Favicon helper ─────────────────────────────────────────── */
function Favicon({ url }) {
  const domain = (() => { try { return new URL(url).hostname; } catch { return ''; } })();
  const src = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  return (
    <img src={src} alt="" width={20} height={20}
      style={{ borderRadius: 4, flexShrink: 0 }}
      onError={e => { e.target.style.display = 'none'; }} />
  );
}

/* ═══════════════════════════════════════════════════════════════
   Dashboard
═══════════════════════════════════════════════════════════════ */
export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, isAuthenticated, isLoading } = useAuthStore();

  const [projects, setProjects] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newOpen, setNewOpen]   = useState(false);
  const [name, setName]         = useState('');
  const [url, setUrl]           = useState('');
  const [creating, setCreating] = useState(false);

  /* Auth guard */
  useEffect(() => {
    if (!isLoading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, isLoading, navigate]);

  /* Load data */
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const [p, a] = await Promise.all([apiClient.get('/projects'), apiClient.get('/analyses')]);
        setProjects(Array.isArray(p) ? p : []);
        setAnalyses(Array.isArray(a) ? a : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [isAuthenticated]);

  /* URL search params redirect */
  useEffect(() => {
    const pid = searchParams.get('new_project'), u = searchParams.get('url');
    if (pid && u) navigate(`/analysis/new?project=${pid}&url=${encodeURIComponent(u)}`);
  }, [searchParams, navigate]);

  /* Create project */
  const handleCreate = async () => {
    if (!url.trim()) return;
    setCreating(true);
    try {
      let u = url.trim();
      if (!u.startsWith('http')) u = 'https://' + u;
      const n = name.trim() || new URL(u).hostname;
      const proj = await apiClient.post('/projects', { name: n, url: u });
      setProjects(prev => [...prev, proj]);
      setNewOpen(false); setName(''); setUrl('');
      navigate(`/analysis/new?project=${proj.project_id}&url=${encodeURIComponent(u)}`);
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  /* Delete project */
  const handleDelete = async (pid, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project and all its analyses?')) return;
    try {
      await apiClient.delete(`/projects/${pid}`);
      setProjects(prev => prev.filter(p => p.project_id !== pid));
      setAnalyses(prev => prev.filter(a => a.project_id !== pid));
    } catch (e) { console.error(e); }
  };

  const lastAnalysis = (pid) =>
    analyses.filter(a => a.project_id === pid)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  const planLabel = { agency: 'Agency', pro: 'Pro', free: 'Free' }[user?.plan] || 'Free';
  const isPro = user?.plan !== 'free';

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: C.font, color: C.text }}>

      {/* ── HEADER ──────────────────────────────────────────── */}
      <header style={{
        background: 'rgba(10,10,15,0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.border}`,
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 28px', height: 58, display: 'flex', alignItems: 'center', gap: 32 }}>

          {/* Logo */}
          <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', flexShrink: 0 }}>
            <div style={{ width: 32, height: 32, background: C.gradient, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, boxShadow: '0 2px 12px rgba(124,58,237,0.4)' }}>
              ✦
            </div>
            <span style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.5px', color: C.text }}>
              SEO Dash <span style={{ color: C.violet }}>360</span>
            </span>
          </Link>

          {/* Nav */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            {NAV.map(item => {
              const active = window.location.pathname === item.to;
              return (
                <Link key={item.to} to={item.to} style={{
                  padding: '6px 13px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                  color: active ? C.text : C.muted,
                  background: active ? 'rgba(124,58,237,0.12)' : 'transparent',
                  textDecoration: 'none', transition: 'all 0.15s',
                }}>
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{
              fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 6,
              background: isPro ? 'rgba(124,58,237,0.2)' : 'rgba(255,255,255,0.06)',
              color: isPro ? '#a78bfa' : C.faint,
              border: `1px solid ${isPro ? 'rgba(124,58,237,0.35)' : 'rgba(255,255,255,0.08)'}`,
              letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>{planLabel}</span>

            {!isPro && (
              <Link to="/pricing" style={{ ...btnGrad, padding: '7px 14px', fontSize: 12, borderRadius: 8, textDecoration: 'none', display: 'inline-block' }}>
                Upgrade ✦
              </Link>
            )}

            {user?.picture
              ? <img src={user.picture} alt="" style={{ width: 30, height: 30, borderRadius: '50%', border: `2px solid ${C.border}` }} />
              : <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(124,58,237,0.15)', border: `2px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: C.violet, fontWeight: 700 }}>
                  {(user?.name || user?.email || '?')[0].toUpperCase()}
                </div>
            }
            <button onClick={async () => { await logout(); navigate('/'); }}
              style={{ padding: '6px 12px', borderRadius: 8, background: 'transparent', border: `1px solid rgba(255,255,255,0.08)`, cursor: 'pointer', color: C.muted, fontSize: 12, fontFamily: C.font, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.text; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = C.muted; }}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* ── MAIN ────────────────────────────────────────────── */}
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 28px 80px' }}>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 100 }}>
            <Spinner style={{ width: 28, height: 28, color: C.violet }} className="animate-spin" />
          </div>
        ) : projects.length === 0 ? (

          /* ── HERO (empty state) ─────────────────────────── */
          <div style={{ textAlign: 'center', paddingTop: 64 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, background: 'rgba(124,58,237,0.12)', border: `1px solid rgba(124,58,237,0.25)`, borderRadius: 20, marginBottom: 28, fontSize: 32 }}>
              ✦
            </div>
            <h1 style={{ fontSize: 48, fontWeight: 800, margin: '0 0 12px', letterSpacing: '-1.5px', background: 'linear-gradient(135deg, #f1f5f9 0%, #94a3b8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Boost your SEO with AI
            </h1>
            <p style={{ fontSize: 17, color: C.muted, margin: '0 0 40px', fontWeight: 400 }}>
              Audit, fix, and optimize your sites automatically
            </p>

            <Dialog open={newOpen} onOpenChange={setNewOpen}>
              <DialogTrigger asChild>
                <button style={{ ...btnGrad, padding: '14px 36px', fontSize: 16, borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 32px rgba(124,58,237,0.35)' }}>
                  <Plus weight="bold" style={{ width: 18, height: 18 }} />
                  New Analysis
                </button>
              </DialogTrigger>
              <DialogContent style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 16 }}>
                <DialogHeader>
                  <DialogTitle style={{ fontWeight: 800, fontSize: 20, color: C.text }}>New project</DialogTitle>
                </DialogHeader>
                <NewProjectForm name={name} setName={setName} url={url} setUrl={setUrl} creating={creating} onSubmit={handleCreate} colors={C} />
              </DialogContent>
            </Dialog>

            <p style={{ marginTop: 20, fontSize: 13, color: C.faint }}>No credit card required · 1 free audit per day</p>

            {/* Quick actions in hero too */}
            <div style={{ marginTop: 72 }}>
              <QuickActions colors={C} />
            </div>
          </div>

        ) : (

          /* ── PROJECTS VIEW ──────────────────────────────── */
          <>
            {/* Page title + CTA */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
              <div>
                <h1 style={{ fontSize: 30, fontWeight: 800, margin: 0, letterSpacing: '-0.8px', color: C.text }}>Dashboard</h1>
                <p style={{ fontSize: 14, color: C.muted, margin: '4px 0 0' }}>
                  {projects.length} project{projects.length !== 1 ? 's' : ''} · {planLabel} plan
                </p>
              </div>
              <Dialog open={newOpen} onOpenChange={setNewOpen}>
                <DialogTrigger asChild>
                  <button style={{ ...btnGrad, padding: '10px 20px', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 16px rgba(124,58,237,0.3)' }}>
                    <Plus weight="bold" style={{ width: 14, height: 14 }} /> New Analysis
                  </button>
                </DialogTrigger>
                <DialogContent style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 16 }}>
                  <DialogHeader>
                    <DialogTitle style={{ fontWeight: 800, fontSize: 20, color: C.text }}>New project</DialogTitle>
                  </DialogHeader>
                  <NewProjectForm name={name} setName={setName} url={url} setUrl={setUrl} creating={creating} onSubmit={handleCreate} colors={C} />
                </DialogContent>
              </Dialog>
            </div>

            {/* Projects grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16, marginBottom: 48 }}>
              {projects.map(proj => {
                const la = lastAnalysis(proj.project_id);
                const score = la?.score ?? la?.seo_score ?? la?.overall_score ?? null;
                return (
                  <div key={proj.project_id}
                    onClick={() => navigate(`/analysis/new?project=${proj.project_id}&url=${encodeURIComponent(proj.url)}`)}
                    style={{ ...card, padding: '20px 22px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: 14 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHi; e.currentTarget.style.boxShadow = C.shadowHi; e.currentTarget.style.background = C.surfaceHover; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = C.shadow; e.currentTarget.style.background = C.surface; }}>

                    {/* Card header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, background: 'rgba(124,58,237,0.1)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(124,58,237,0.2)' }}>
                          <Favicon url={proj.url} />
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, letterSpacing: '-0.3px' }}>{proj.name}</div>
                          <div style={{ fontSize: 11, color: C.faint }}>{proj.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</div>
                        </div>
                      </div>
                      <ScoreBadge score={score} />
                    </div>

                    {/* Last analysis */}
                    {la && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                        <StatusIcon status={la.status} />
                        <span style={{ fontSize: 12, color: C.muted, flex: 1 }}>
                          Last audit: {new Date(la.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                        </span>
                        {la.report_id && <ArrowUpRight style={{ width: 11, height: 11, color: C.faint }} />}
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/analysis/new?project=${proj.project_id}&url=${encodeURIComponent(proj.url)}`); }}
                        style={{ flex: 1, ...btnGrad, padding: '8px 0', fontSize: 13, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        View Audit <ArrowRight style={{ width: 12, height: 12 }} />
                      </button>
                      <button
                        onClick={e => handleDelete(proj.project_id, e)}
                        style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 9, cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.18)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}>
                        <Trash style={{ width: 13, height: 13 }} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Add new project card */}
              <div
                onClick={() => setNewOpen(true)}
                style={{ ...card, padding: '20px 22px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 160, borderStyle: 'dashed' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.violet; e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; }}>
                <div style={{ width: 40, height: 40, background: 'rgba(124,58,237,0.1)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Plus weight="bold" style={{ width: 18, height: 18, color: C.violet }} />
                </div>
                <span style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>Add new project</span>
              </div>
            </div>

            {/* Quick actions */}
            <QuickActions colors={C} />
          </>
        )}
      </main>
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────────── */

function NewProjectForm({ name, setName, url, setUrl, creating, onSubmit, colors: C }) {
  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${C.border}`, borderRadius: 10,
    padding: '10px 13px', fontSize: 14, color: C.text,
    outline: 'none', boxSizing: 'border-box', fontFamily: C.font,
    transition: 'border-color 0.15s',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
      <div>
        <Label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Project name (optional)
        </Label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="My Website" style={inputStyle}
          onFocus={e => e.target.style.borderColor = C.violet}
          onBlur={e => e.target.style.borderColor = C.border} />
      </div>
      <div>
        <Label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Website URL *
        </Label>
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://yoursite.com"
          onKeyDown={e => e.key === 'Enter' && onSubmit()} style={{ ...inputStyle, borderColor: C.violet }}
          onFocus={e => e.target.style.borderColor = '#a78bfa'}
          onBlur={e => e.target.style.borderColor = C.violet} />
      </div>
      <button onClick={onSubmit} disabled={creating || !url.trim()}
        style={{ ...{ background: C.gradient, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: creating || !url.trim() ? 'not-allowed' : 'pointer', fontFamily: C.font }, padding: '12px 0', fontSize: 14, opacity: creating || !url.trim() ? 0.5 : 1, boxShadow: '0 4px 16px rgba(124,58,237,0.3)', transition: 'opacity 0.15s' }}>
        {creating ? 'Starting…' : 'Start Analysis →'}
      </button>
    </div>
  );
}

function QuickActions({ colors: C }) {
  return (
    <div>
      <p style={{ fontSize: 11, fontWeight: 700, color: C.faint, margin: '0 0 16px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Quick Actions
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {QUICK.map(q => (
          <Link key={q.to} to={q.to} style={{ textDecoration: 'none' }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '18px 16px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: C.shadow }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.violet; e.currentTarget.style.boxShadow = C.shadowHi; e.currentTarget.style.background = C.surfaceHover; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = C.shadow; e.currentTarget.style.background = C.surface; }}>
              <div style={{ width: 38, height: 38, background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <q.icon style={{ width: 17, height: 17, color: C.violet }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>{q.label}</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4 }}>{q.desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
